'use client'

import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'
import { IGetCityQueryData } from '@/api/cities/get-city-query'
import {
    IGetRestaurantsQueryParams,
    useGetRestaurantsInfiniteQuery,
} from '@/api/restaurants/get-restaurants-query'
import {
    GetCityRestaurantResponseMeta,
    IListRestaurant,
} from '@/api/restaurants/types/restaurant-types'
import { useRestaurantCitySearchManager } from '@/features/restaurants/restaurants-city-search/utils/use-restaurant-city-search-manager'
import { useRouter } from '@/setup/i18n/use-router'
import { RestaurantQuerySorting } from '@/setup/urls/city-route'
import { noOp } from '@/utils/functions/noOp'

export interface IRestaurantCitySearchQuerySetters {
    setCitySlug: (citySlug: string) => void
    setName: (name: string) => void
    setTagSlugs: (tagSlugs: string[]) => void
    setSortBy: (sortBy: RestaurantQuerySorting) => void
    setOpenNow: (openNow: boolean) => void
}

export interface IRestaurantCitySearchContext {
    restaurants: IListRestaurant[] | undefined
    currency: GetCityRestaurantResponseMeta['currency']
    query: IGetRestaurantsQueryParams
    setters: IRestaurantCitySearchQuerySetters
    city: IGetCityQueryData | undefined
    fetchNextPage: () => void
    isFetchingNextPage: boolean
    hasNextPage: boolean
}

const RestaurantCitySearchContext = createContext<IRestaurantCitySearchContext>({
    restaurants: undefined,
    currency: undefined,
    city: undefined,
    query: {
        citySlug: undefined,
        name: undefined,
        tagSlugs: undefined,
    },
    setters: {
        setOpenNow: noOp,
        setCitySlug: noOp,
        setName: noOp,
        setTagSlugs: noOp,
        setSortBy: noOp,
    },
    fetchNextPage: noOp,
    isFetchingNextPage: false,
    hasNextPage: false,
})

export const RestaurantCitySearchProvider = ({
    children,
    city,
}: {
    children: ReactNode
    city: IGetCityQueryData | undefined
}) => {
    const { query, setters } = useRestaurantCitySearchManager()

    const {
        data: getInifniteRestaurantsResponse,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useGetRestaurantsInfiniteQuery(query)

    const router = useRouter()

    /**
     * Sync the city slug with the one from the response
     * The city slug from the response is our source of truth.
     */
    useEffect(() => {
        if (!getInifniteRestaurantsResponse?.pages?.length) return

        let restaurantCitySlug: string | undefined

        for (const pageResponse of getInifniteRestaurantsResponse.pages) {
            if (restaurantCitySlug) break

            restaurantCitySlug = pageResponse.data.find(
                (restaurant) => restaurant.citySlug
            )?.citySlug
        }

        if (!restaurantCitySlug) return

        // Sync the city slug with the one from the response
        const isDifferentCitySlug = restaurantCitySlug !== query.citySlug

        if (isDifferentCitySlug) {
            router.replace({
                pattern: '/restaurants/[citySlug]',
                params: {
                    citySlug: restaurantCitySlug,
                },
            })
        }
    }, [getInifniteRestaurantsResponse, query.citySlug, router])

    const restaurants = useMemo(
        () => getInifniteRestaurantsResponse?.pages.flatMap((res) => res.data),
        [getInifniteRestaurantsResponse]
    )

    const currency = useMemo(
        () => getInifniteRestaurantsResponse?.pages[0]?.meta?.currency,
        [getInifniteRestaurantsResponse]
    )

    return (
        <RestaurantCitySearchContext.Provider
            value={{
                query,
                restaurants,
                currency,
                setters,
                city,
                fetchNextPage,
                isFetchingNextPage,
                hasNextPage,
            }}
        >
            {children}
        </RestaurantCitySearchContext.Provider>
    )
}

export const useRestaurantCitySearchContext = () => {
    return useContext(RestaurantCitySearchContext)
}
